import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { EmptyState } from "@/components/dashboard/common/PageShell";
import {
  Globe, Instagram, Youtube, Music2, Twitter, Facebook, Linkedin, Twitch, MessageCircle, Send, Camera, AtSign, Pin, Loader2,
  Plus, Trash2, Upload, ImageIcon, Tag, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { useMediaUpload } from "@/hooks/platform/useMediaUpload";
import { useUpdateProfile } from "@/hooks/platform/useProfile";
import { getProfileBanner, setProfileBanner, showcaseGallery } from "@/lib/profileBanner";
import MediaImage from "@/components/platform/MediaImage";
import { useMediaUrl } from "@/lib/storageUrl";

export interface GalleryItem { url: string; caption?: string }
export interface PriceItem { name: string; price: string; unit?: string; details?: string }
export type SocialLinks = Record<string, string>;

export const SOCIAL_FIELDS: Array<{ key: string; label: string; icon: React.ElementType; placeholder: string }> = [
  { key: "instagram", label: "Instagram", icon: Instagram, placeholder: "https://instagram.com/yourname" },
  { key: "x", label: "X / Twitter", icon: Twitter, placeholder: "https://x.com/yourname" },
  { key: "tiktok", label: "TikTok", icon: Music2, placeholder: "https://tiktok.com/@yourname" },
  { key: "youtube", label: "YouTube", icon: Youtube, placeholder: "https://youtube.com/@yourname" },
  { key: "spotify", label: "Spotify", icon: Music2, placeholder: "https://open.spotify.com/artist/..." },
  { key: "soundcloud", label: "SoundCloud", icon: Music2, placeholder: "https://soundcloud.com/yourname" },
  { key: "facebook", label: "Facebook", icon: Facebook, placeholder: "https://facebook.com/yourpage" },
  { key: "threads", label: "Threads", icon: AtSign, placeholder: "https://threads.net/@yourname" },
  { key: "linkedin", label: "LinkedIn", icon: Linkedin, placeholder: "https://linkedin.com/in/yourname" },
  { key: "snapchat", label: "Snapchat", icon: Camera, placeholder: "https://snapchat.com/add/yourname" },
  { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, placeholder: "https://wa.me/27XXXXXXXXX" },
  { key: "telegram", label: "Telegram", icon: Send, placeholder: "https://t.me/yourname" },
  { key: "twitch", label: "Twitch", icon: Twitch, placeholder: "https://twitch.tv/yourname" },
  { key: "pinterest", label: "Pinterest", icon: Pin, placeholder: "https://pinterest.com/yourname" },
  { key: "applemusic", label: "Apple Music", icon: Music2, placeholder: "https://music.apple.com/artist/..." },
  { key: "audiomack", label: "Audiomack", icon: Music2, placeholder: "https://audiomack.com/yourname" },
  { key: "mixcloud", label: "Mixcloud", icon: Music2, placeholder: "https://mixcloud.com/yourname" },
  { key: "bandcamp", label: "Bandcamp", icon: Music2, placeholder: "https://yourname.bandcamp.com" },
];

const normalise = (raw: string) => {
  const v = raw.trim();
  if (!v) return "";
  return /^https?:\/\//i.test(v) ? v : `https://${v}`;
};

export function parseGallery(value: unknown): GalleryItem[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((v) => {
    if (typeof v === "string") return [{ url: v }];
    if (v && typeof v === "object" && typeof (v as GalleryItem).url === "string") return [v as GalleryItem];
    return [];
  });
}

export function parsePricing(value: unknown): PriceItem[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is PriceItem => !!v && typeof v === "object" && typeof (v as PriceItem).name === "string");
}

export function parseSocials(value: unknown): SocialLinks {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  const out: SocialLinks = {};
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) if (typeof v === "string" && v) out[k] = v;
  return out;
}

const isVideo = (url: string) => /\.(mp4|webm|mov)$/i.test(url);

const ShowcaseVideo = ({ url, className, muted }: { url: string; className?: string; muted?: boolean }) => {
  const resolved = useMediaUrl(url);
  return <video src={resolved ?? undefined} controls={!muted} muted={muted} className={className} />;
};

interface Props {
  isOwn: boolean;
  kind: "creator" | "venue";
  website: string | null;
  socials: SocialLinks;
  gallery: GalleryItem[];
  pricing: PriceItem[];
}

/**
 * Public showcase block for creator and venue profiles:
 * bio links, portfolio / venue photos and a published rate card.
 */
const ProfileShowcase = ({ isOwn, kind, website, socials, gallery, pricing }: Props) => {
  const [open, setOpen] = useState(false);
  const updateProfile = useUpdateProfile();
  const { upload, uploading } = useMediaUpload("creator-media");

  const [site, setSite] = useState(website ?? "");
  const [links, setLinks] = useState<SocialLinks>(socials);
  const [items, setItems] = useState<GalleryItem[]>(gallery);
  const [rates, setRates] = useState<PriceItem[]>(pricing);

  useEffect(() => {
    if (open) {
      setSite(website ?? "");
      setLinks(socials);
      setItems(showcaseGallery(gallery));
      setRates(pricing);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const galleryTitle = kind === "venue" ? "Venue photos" : "Portfolio work";
  const galleryHint = kind === "venue"
    ? "Show the room, the stage, the bar and past nights so bookers know what they're getting."
    : "Show your best sets, shoots and releases so venues can see your work.";

  const addFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    for (const file of Array.from(files).slice(0, 8)) {
      const url = await upload(file);
      if (url) setItems((cur) => [...cur, { url }]);
    }
  };

  const save = async () => {
    const cleanedLinks: SocialLinks = {};
    for (const [k, v] of Object.entries(links)) {
      const url = normalise(v);
      if (url) cleanedLinks[k] = url;
    }
    try {
      await updateProfile.mutateAsync({
        website: normalise(site) || null,
        social_links: cleanedLinks,
        gallery: setProfileBanner(items.filter((i) => i.url), getProfileBanner(gallery)),
        pricing: rates.filter((r) => r.name.trim()),
      } as never);
      toast.success("Profile showcase updated");
      setOpen(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not save");
    }
  };

  const activeSocials = SOCIAL_FIELDS.filter((f) => socials[f.key]);
  const hasAnything = !!website || activeSocials.length > 0 || gallery.length > 0 || pricing.length > 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-white">{kind === "venue" ? "Venue showcase" : "Creator showcase"}</h3>
        {isOwn && (
          <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
            <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit showcase
          </Button>
        )}
      </div>

      {!hasAnything ? (
        <EmptyState
          icon={ImageIcon}
          title={isOwn ? "Your showcase is empty" : "Nothing published yet"}
          description={isOwn ? `Add ${galleryTitle.toLowerCase()}, links and pricing so people can book you.` : "This account hasn't published photos, links or pricing yet."}
        />
      ) : (
        <>
          {(website || activeSocials.length > 0) && (
            <div className="flex flex-wrap gap-2">
              {website && (
                <a href={website} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-xs text-white hover:border-zinc-600">
                  <Globe className="h-3.5 w-3.5" /> Website
                </a>
              )}
              {activeSocials.map((f) => (
                <a key={f.key} href={socials[f.key]} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 rounded-full border border-zinc-800 bg-zinc-950 px-3 py-1.5 text-xs text-white hover:border-zinc-600">
                  <f.icon className="h-3.5 w-3.5" /> {f.label}
                </a>
              ))}
            </div>
          )}

          {gallery.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">{galleryTitle}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {gallery.map((g, i) => (
                  <figure key={`${g.url}-${i}`} className="relative aspect-square overflow-hidden rounded-lg bg-zinc-900">
                    {isVideo(g.url)
                      ? <ShowcaseVideo url={g.url} className="h-full w-full object-cover" />
                      : <MediaImage src={g.url} alt={g.caption || `${galleryTitle} ${i + 1}`} loading="lazy" className="h-full w-full object-cover" />}
                    {g.caption && (
                      <figcaption className="absolute inset-x-0 bottom-0 bg-black/70 px-2 py-1 text-[11px] text-white truncate">{g.caption}</figcaption>
                    )}
                  </figure>
                ))}
              </div>
            </div>
          )}

          {pricing.length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">Pricing</p>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {pricing.map((p, i) => (
                  <Card key={`${p.name}-${i}`} className="bg-zinc-950 border-zinc-800">
                    <CardContent className="p-4 space-y-1.5">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-white">{p.name}</p>
                        <Badge variant="secondary" className="shrink-0"><Tag className="h-3 w-3 mr-1" />{p.price}</Badge>
                      </div>
                      {p.unit && <p className="text-[11px] text-gray-500">{p.unit}</p>}
                      {p.details && <p className="text-xs text-gray-300 whitespace-pre-wrap">{p.details}</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit {kind === "venue" ? "venue" : "creator"} showcase</DialogTitle></DialogHeader>

          <div className="space-y-6">
            <div className="space-y-3">
              <p className="text-sm font-semibold text-white">Bio links</p>
              <div>
                <Label className="text-white text-xs">Website</Label>
                <Input value={site} onChange={(e) => setSite(e.target.value)} placeholder="https://yoursite.com"
                  className="bg-zinc-900 border-zinc-800 mt-1 text-white" />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {SOCIAL_FIELDS.map((f) => (
                  <div key={f.key}>
                    <Label className="text-white text-xs flex items-center gap-1.5"><f.icon className="h-3.5 w-3.5" />{f.label}</Label>
                    <Input
                      value={links[f.key] ?? ""}
                      onChange={(e) => setLinks((l) => ({ ...l, [f.key]: e.target.value }))}
                      placeholder={f.placeholder}
                      className="bg-zinc-900 border-zinc-800 mt-1 text-white"
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-white">{galleryTitle}</p>
                <label>
                  <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} />
                  <span className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-zinc-800 px-3 py-1.5 text-xs text-white hover:bg-zinc-900">
                    {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload
                  </span>
                </label>
              </div>
              <p className="text-xs text-gray-500">{galleryHint}</p>
              {items.length === 0 ? (
                <p className="text-xs text-gray-600">No photos yet.</p>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {items.map((g, i) => (
                    <div key={`${g.url}-${i}`} className="space-y-1">
                      <div className="relative aspect-square overflow-hidden rounded-lg bg-zinc-900">
                        {isVideo(g.url)
                          ? <ShowcaseVideo url={g.url} className="h-full w-full object-cover" muted />
                          : <MediaImage src={g.url} alt="" className="h-full w-full object-cover" />}
                        <button
                          type="button"
                          aria-label="Remove photo"
                          onClick={() => setItems((cur) => cur.filter((_, idx) => idx !== i))}
                          className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white hover:bg-black"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      <Input
                        value={g.caption ?? ""}
                        placeholder="Caption"
                        onChange={(e) => setItems((cur) => cur.map((it, idx) => idx === i ? { ...it, caption: e.target.value } : it))}
                        className="bg-zinc-900 border-zinc-800 text-white h-8 text-xs"
                      />
                    </div>
                  ))}
                </div>
              )}
              <div className="flex gap-2">
                <Input
                  placeholder="…or paste an image / video URL"
                  className="bg-zinc-900 border-zinc-800 text-white"
                  onKeyDown={(e) => {
                    const el = e.currentTarget;
                    if (e.key === "Enter" && el.value.trim()) {
                      e.preventDefault();
                      setItems((cur) => [...cur, { url: normalise(el.value) }]);
                      el.value = "";
                    }
                  }}
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-white">Pricing</p>
                <Button size="sm" variant="outline" onClick={() => setRates((r) => [...r, { name: "", price: "", unit: "", details: "" }])}>
                  <Plus className="h-3.5 w-3.5 mr-1.5" /> Add package
                </Button>
              </div>
              {rates.length === 0 && <p className="text-xs text-gray-600">No packages yet.</p>}
              {rates.map((r, i) => (
                <div key={i} className="rounded-lg border border-zinc-800 p-3 space-y-2">
                  <div className="grid gap-2 sm:grid-cols-3">
                    <Input value={r.name} placeholder={kind === "venue" ? "Weekend hire" : "2-hour DJ set"}
                      onChange={(e) => setRates((cur) => cur.map((it, idx) => idx === i ? { ...it, name: e.target.value } : it))}
                      className="bg-zinc-900 border-zinc-800 text-white" />
                    <Input value={r.price} placeholder="R5 000"
                      onChange={(e) => setRates((cur) => cur.map((it, idx) => idx === i ? { ...it, price: e.target.value } : it))}
                      className="bg-zinc-900 border-zinc-800 text-white" />
                    <Input value={r.unit ?? ""} placeholder="per night"
                      onChange={(e) => setRates((cur) => cur.map((it, idx) => idx === i ? { ...it, unit: e.target.value } : it))}
                      className="bg-zinc-900 border-zinc-800 text-white" />
                  </div>
                  <Textarea value={r.details ?? ""} rows={2} placeholder="What's included"
                    onChange={(e) => setRates((cur) => cur.map((it, idx) => idx === i ? { ...it, details: e.target.value } : it))}
                    className="bg-zinc-900 border-zinc-800 text-white" />
                  <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300"
                    onClick={() => setRates((cur) => cur.filter((_, idx) => idx !== i))}>
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" /> Remove
                  </Button>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={updateProfile.isPending || uploading}>
              {updateProfile.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Save showcase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProfileShowcase;
